// App category selected on the Console Apps page. All shows Business and
// Security together; Business holds the organization-management surface and its
// apps; Security holds the personal vault apps. The last explicit choice is
// remembered per user.
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useSession } from "@/lib/session";

export const APP_CATEGORIES = ["all", "business", "security"] as const;
export type AppCategory = (typeof APP_CATEGORIES)[number];

const CATEGORY_KEY_PREFIX = "sylviq.console.app-category:";
const DEFAULT_CATEGORY: AppCategory = "all";

interface AppCategoryValue {
  category: AppCategory;
  setCategory: (category: AppCategory) => void;
}

const AppCategoryContext = createContext<AppCategoryValue | null>(null);

function isAppCategory(value: string | null): value is AppCategory {
  return value === "all" || value === "business" || value === "security";
}

function readStoredCategory(userId: string): AppCategory | null {
  try {
    const value = localStorage.getItem(`${CATEGORY_KEY_PREFIX}${userId}`);
    return isAppCategory(value) ? value : null;
  } catch {
    return null;
  }
}

export function AppCategoryProvider({ children }: { children: ReactNode }) {
  const { state } = useSession();
  const identity = state.status === "authenticated" ? state.user.id : "anonymous";
  return <AppCategoryScope key={identity}>{children}</AppCategoryScope>;
}

function AppCategoryScope({ children }: { children: ReactNode }) {
  const { state } = useSession();
  const userId = state.status === "authenticated" ? state.user.id : null;
  const [choice, setChoice] = useState<AppCategory | null>(null);

  const category = useMemo<AppCategory>(() => {
    if (choice) return choice;
    if (userId) {
      const stored = readStoredCategory(userId);
      if (stored) return stored;
    }
    return DEFAULT_CATEGORY;
  }, [choice, userId]);

  const setCategory = useCallback(
    (next: AppCategory) => {
      setChoice(next);
      if (!userId) return;
      try {
        localStorage.setItem(`${CATEGORY_KEY_PREFIX}${userId}`, next);
      } catch {
        // Storage blocked: the choice still applies for this session.
      }
    },
    [userId],
  );

  return (
    <AppCategoryContext.Provider value={{ category, setCategory }}>
      {children}
    </AppCategoryContext.Provider>
  );
}

export function useAppCategory(): AppCategoryValue {
  const value = useContext(AppCategoryContext);
  if (!value) {
    throw new Error("useAppCategory() must be used inside AppCategoryProvider");
  }
  return value;
}
